import {
	ChangeEventHandler,
	FC,
	useState,
	ClipboardEvent,
	ChangeEvent,
	KeyboardEvent,
	useRef,
	useLayoutEffect,
} from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Input } from 'antd';

import { usePreventScroll } from 'hooks';

import styles from './index.module.scss';

type CodeFieldProps = {
	name: string;
	fields: number;
	onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
	disabled: boolean;
};

export const CodeField: FC<CodeFieldProps> = ({ fields, onChange, disabled }) => {
	const [segments, setSegments] = useState(new Array(fields).fill(''));

	const inputRef = useRef(null);

	const onPaste = (event: ClipboardEvent) => {
		const pasted = event.clipboardData.getData('text/plain');

		const pastedData = pasted.split('').slice(0, fields);

		const newSegments = segments.map((item, i) => {
			return pastedData[i] || '';
		});

		// @ts-ignore
		const [fieldName, fieldIndex] = event.target.name.split('-');

		const currentSibling = document.querySelector(
			`input[name=${fieldName}-${parseInt(fieldIndex, 10)}]`,
		);

		if (currentSibling !== null && pasted.length === fields) {
			// @ts-ignore
			currentSibling.blur();
		}

		setSegments(newSegments);

		if (onChange) {
			onChange(
				// @ts-ignore
				newSegments.reduce((code, item) => {
					return `${code}${item}`;
				}, ''),
			);
		}
	};

	const changeInput = (e: ChangeEvent) => {
		// @ts-ignore
		if (!/[0-9]/.test(+e.target.value)) {
			return;
		}
		// @ts-ignore

		// @ts-ignore
		if (e.nativeEvent.inputType === 'insertFromPaste') return;
		// @ts-ignore
		const { maxLength, name, value } = e.target;

		const [fieldName, fieldIndex] = name.split('-');

		const newSegments = segments;

		if (value.length <= maxLength) {
			newSegments[fieldIndex] = value;
		}

		setSegments(newSegments);

		if (value.length >= maxLength) {
			if (parseInt(fieldIndex, 10) < 6) {
				const nextSibling = document.querySelector(
					`input[name=${fieldName}-${parseInt(fieldIndex, 10) + 1}]`,
				);

				if (nextSibling !== null) {
					// @ts-ignore
					nextSibling.focus();
				}

				if (fieldIndex === '5') {
					const lastSibling = document.querySelector(
						`input[name=${fieldName}-${parseInt(fieldIndex, 10)}]`,
					);

					if (lastSibling !== null) {
						// @ts-ignore
						lastSibling.blur();
					}
				}
			}
		}

		if (onChange) {
			onChange(
				newSegments.reduce((code, item) => {
					return `${code}${item}`;
				}, ''),
			);
		}
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Backspace') {
			// @ts-ignore
			const { name, value } = e.target;
			const [fieldName, fieldIndex] = name.split('-');

			const newSegments = segments;

			newSegments[fieldIndex] = value;

			setSegments(newSegments);

			if (value.length === 0) {
				if (parseInt(fieldIndex, 10) < 6) {
					const nextSibling = document.querySelector(
						`input[name=${fieldName}-${parseInt(fieldIndex, 10) - 1}]`,
					);

					if (nextSibling !== null) {
						// @ts-ignore
						nextSibling.focus();
					}
				}
			}
		}
	};

	usePreventScroll();

	useLayoutEffect(() => {
		// @ts-ignore
		inputRef.current.focus();
	}, []);

	return (
		<div className={styles.reactCodeInput}>
			{segments.map((item, i) => (
				<Input
					ref={i === 0 ? inputRef : undefined}
					key={`code-${i}`}
					name={`code-${i}`}
					value={item}
					type="text"
					onPaste={onPaste}
					maxLength={1}
					onChange={changeInput}
					onKeyDown={onKeyDown}
					inputMode="numeric"
				/>
			))}

			{disabled && (
				<LoadingOutlined spin style={{ position: 'absolute', top: '20px', marginLeft: '5px' }} />
			)}
		</div>
	);
};
